import React, { useContext, useMemo } from "react";
import { Col, Row } from "antd";
import { FormattedMessage } from "react-intl";
import { Swiper, SwiperSlide } from "swiper/react/swiper-react.js";
import styled from "styled-components";
import styles from "src/styles";

// Contexts
import { OffsetLayoutContext } from "src/contexts/layout.context";

// Helpers
import { calculateLayout } from "src/helpers/ui.helper";

// Components
import PromotionCard from "src/components/promotions/PromotionCard";

// Assets
import { ReactComponent as ArrowRightIcon } from "src/assets/icons/arrow-right.svg";

type Props = {
    title?: string;
    dataSource?: Promotion[];
    showSeeAll?: boolean;
    onClick?: (item: Promotion) => void;
    onClickSeeAll?: () => void;
};

const responsiveConfig: Record<ScreenSize, any> = {
    xs: {
        breakpoint: 1,
        slidesPerView: 1.15,
        spaceBetween: 20,
    },
    sm: {
        breakpoint: 576,
        slidesPerView: 1.75,
        spaceBetween: 12,
    },
    md: {
        breakpoint: 768,
        slidesPerView: 2.15,
        spaceBetween: 30,
    },
    lg: {
        breakpoint: 992,
        slidesPerView: 2.75,
        spaceBetween: 30,
    },
    xl: {
        breakpoint: 1200,
        slidesPerView: 3.15,
        spaceBetween: 30,
    },
    xxl: {
        breakpoint: 1600,
        slidesPerView: 4.15,
        spaceBetween: 30,
    },
};

const PromotionsCarousel = ({
    title,
    dataSource = [],
    showSeeAll = true,
    onClick = () => {},
    onClickSeeAll = () => {},
}: Props) => {
    const offsetLayout = useContext(OffsetLayoutContext);
    const layout = calculateLayout(offsetLayout, 24);

    const breakpoints = useMemo(
        () =>
            Object.keys(responsiveConfig).reduce((result, key) => {
                const item = responsiveConfig[key as ScreenSize];
                const { breakpoint, ...obj } = item;

                return {
                    ...result,
                    [breakpoint]: {
                        ...obj,
                        slidesPerView:
                            obj.slidesPerView >= dataSource.length
                                ? Math.floor(obj.slidesPerView)
                                : obj.slidesPerView,
                    },
                };
            }, {}),
        [dataSource.length]
    );

    return (
        <DivStyled className="promotions-carousel">
            <Row>
                <Col {...layout}>
                    <Row justify="space-between" align="middle">
                        <Col xs={18} md={16}>
                            <span className="title">{title}</span>
                        </Col>
                        {showSeeAll && (
                            <Col xs={6} md={8} className="nav-actions">
                                <span className="text" onClick={onClickSeeAll}>
                                    <FormattedMessage id="promotionsCarousel.seeAll" />
                                </span>
                                <ArrowRightIcon onClick={onClickSeeAll} />
                            </Col>
                        )}
                    </Row>
                </Col>
            </Row>
            <Row>
                <Col
                    xs={{ offset: offsetLayout.xs, span: 24 - offsetLayout.xs }}
                    sm={{ offset: offsetLayout.sm, span: 24 - offsetLayout.sm }}
                    md={{ offset: offsetLayout.md, span: 24 - offsetLayout.md }}
                    lg={{ offset: offsetLayout.lg, span: 24 - offsetLayout.lg }}
                    xl={{ offset: offsetLayout.xl, span: 24 - offsetLayout.xl }}
                    xxl={{
                        offset: offsetLayout.xxl,
                        span: 24 - offsetLayout.xxl,
                    }}
                >
                    <Swiper breakpoints={breakpoints}>
                        {dataSource.map((item: Promotion) => (
                            <SwiperSlide key={item.id}>
                                <PromotionCard
                                    {...item}
                                    onClick={() => onClick(item)}
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </Col>
            </Row>
        </DivStyled>
    );
};

const DivStyled = styled.div`
    &.promotions-carousel {
        .title {
            font-style: normal;
            font-weight: 500;
            font-size: 32px;
            line-height: 1.6;
            color: ${styles.fontColorWhite};

            @media screen and (max-width: 767px) {
                font-size: 24px;
            }
        }

        .nav-actions {
            display: flex;
            justify-content: flex-end;
            align-items: center;

            .text {
                @media screen and (max-width: 767px) {
                    display: none;
                }
            }

            & > span {
                font-style: normal;
                font-weight: 500;
                font-size: 16px;
                line-height: 26px;
                color: ${styles.fontColorWhite};
                cursor: pointer;
            }

            & > svg {
                cursor: pointer;
                margin-left: 12px;

                path {
                    fill: ${styles.fontColorWhite};
                }
            }
        }

        .swiper {
            margin-top: 30px;
        }
    }
`;

export default PromotionsCarousel;
